import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AxiosError } from "axios";
import { Tag } from "../../interfaces/Tag";
import { Rule, RuleType } from "../../interfaces/Rule";
import { CoObjectItem } from "../../interfaces/CoObject";
import { TgkActionMessage, TgkMessageModel } from "../../generic/chat/types";
import {
  bot,
  newBotMsg,
  newBotSystemMsg,
  newUserActionLabel,
  newUserActionMsg,
  newUserMsg,
} from "../../generic/chat/ChatBot";
import {
  executeValidationAgent,
  LaunchResult,
  sendLaunchResults,
  ValidationAgentConversationMessage,
  ValidationAgentResponse,
} from "../../../api/document/ValidationAgent";
import {
  formatMessage,
  formatMessageDefault,
  formatMessageWithParamsDefault,
} from "../../../utils/helpers/LanguageHelper";
import { addTagToCustomXml, EditTagResult, editTagInCustomXml } from "../../../utils/helpers/CustomXmlCoTagHelper";
import { remapAndPersistRules, resolveTransitiveRemaps } from "../../../utils/helpers/TagMergeHelper";
import { addRuleToCustomXml, editRuleInCustomXml } from "../../../utils/helpers/CustomXmlCoRuleHelper";
import { generatePrefixedId } from "../../../utils/helpers/IdHelper";
import { extractTagsFromScript } from "../../../utils/helpers/TagsHelper";
import { runRules, ConsistencyTagErrorKind } from "../../../utils/helpers/RulesRunnerHelper";
import { useAppDispatch, useAppSelector, useAppStore } from "../../../redux/hooks/hooks";
import {
  addMessage,
  applyRemapToStagedRuleEdits,
  clearPendingActionData,
  clearStagedRuleEdits,
  clearStagedTagEdits,
  clearStagingArea,
  PendingActionData,
  resetMessages,
  setLaunchRuleIds,
  StagedEdit,
} from "../../../redux/feature/chat/askai-chat-slice";
import { AppDispatch } from "../../../redux/store/store";
import {
  CAPTION_ASKAI_DISCARDED_RULE_EDIT,
  CAPTION_ASKAI_DISCARDED_TAG_EDIT,
  CAPTION_BUTTON_CHECK_AND_EDIT,
  CAPTION_BUTTON_RUN_RULE,
  CAPTION_BUTTON_RUN_RULES,
  CAPTION_CHAT_ERROR_GENERIC,
  CAPTION_CHAT_TIMEOUT,
  CAPTION_ERROR_RULE_SIMULATION,
  CAPTION_ERROR_VALIDATION_AREA,
  CAPTION_LABEL_SELECT_OPTION,
  CAPTION_SAVED_VALIDATION_AREA,
} from "./validationCaptions";
import {
  buildFallbackLaunchInterpretation,
  createLaunchResultsMessage,
  DeletedTagReferenceError,
  getRunResultErrorReason,
  processToolCalls,
  ProcessToolCallsResult,
  UnresolvableTagError,
} from "./ToolRunner";
import { RunResult } from "../../rules/RulesResultPanel";
import { wrapSystemMessage } from "./systemMessage";
import {
  prepareRagIndex,
  RagIndexStatus,
  RagIndexStatusChangedEvent,
  RagIndexStatusValue,
} from "../../../api/document/RagIndexing";
import { subscribeToHost } from "../../../utils/helpers/EventsHelper";

export interface AskAiProps {
  isShown: boolean;
  onHide: () => void;
  documentId?: string;
  coObjects?: CoObjectItem[];
  tags?: Tag[];
  rules?: Rule[];
  updateTags?: (tags: Tag[]) => void;
  updateRules?: (rules: Rule[]) => void;
}

export interface AskAiResult {
  handleBack: () => void;
  messages: TgkMessageModel[];
  loading: boolean;
  onMessageSend: (message: TgkMessageModel) => void;
  pendingActionData: PendingActionData | null;
  hasStagedReviewData: boolean;
  showReviewSaveModal: boolean;
  onReviewModalClose: () => void;
  onReviewSave: (reviewedData: PendingActionData) => void;
  resetChat: () => void;
  ragIndexStatus: RagIndexStatusValue | null;
  ragIndexSentAt: string | null;
  ragIndexUploadObserved: boolean;
}

export interface RagIndexUiState extends RagIndexStatus {
  uploadObserved: boolean;
}

export const EMPTY_RAG_INDEX_UI_STATE: RagIndexUiState = {
  status: "none",
  sentAt: null,
  statusUpdatedAt: null,
  uploadObserved: false,
};

// FR-004: bounded re-subscribe cadence for terminal RAG SSE failures (readyState CLOSED). Finite
// attempts with exponential backoff avoid a busy reconnect loop against a persistently failing
// endpoint; the count resets after a successful reopen. Exported so tests assert against the
// same source of truth instead of duplicating magic numbers.
export const RAG_SSE_RESUBSCRIBE_MAX_ATTEMPTS = 5;
export const RAG_SSE_RESUBSCRIBE_BASE_DELAY_MS = 2000;
export const RAG_SSE_RESUBSCRIBE_MAX_DELAY_MS = 30000;

function isOlderRagIndexStatus(current: RagIndexUiState, incoming: RagIndexStatus): boolean {
  if (current.statusUpdatedAt && !incoming.statusUpdatedAt) {
    return true;
  }

  if (!current.statusUpdatedAt || !incoming.statusUpdatedAt) {
    return false;
  }

  return Date.parse(incoming.statusUpdatedAt) < Date.parse(current.statusUpdatedAt);
}

export function mergeRagIndexStatus(
  current: RagIndexUiState,
  incoming: RagIndexStatus,
  seedSentAtFromSnapshot = false
): RagIndexUiState {
  if (incoming.status === "ABORTED" || isOlderRagIndexStatus(current, incoming)) {
    return current;
  }

  if (incoming.status === "none") {
    return EMPTY_RAG_INDEX_UI_STATE;
  }

  const sentAt =
    incoming.status === "SUCCESS"
      ? (incoming.sentAt ?? current.sentAt)
      : incoming.status === "IN_PROGRESS" || incoming.status === "FAILED" || incoming.status === "TIMEOUT"
        ? (current.sentAt ?? (seedSentAtFromSnapshot ? incoming.sentAt : null))
        : current.sentAt;

  return {
    status: incoming.status,
    sentAt,
    statusUpdatedAt: incoming.statusUpdatedAt,
    uploadObserved: current.uploadObserved || incoming.status === "IN_PROGRESS",
  };
}

function toValidationAgentConversationMessage(message: TgkMessageModel): ValidationAgentConversationMessage | null {
  if (message.messageType !== "message") {
    return null;
  }

  if (message.systemMessage) {
    return null;
  }

  const content = message.html ?? message.text ?? "";
  if (!content.trim()) {
    return null;
  }

  return {
    role: message.author?.id === bot.id ? "assistant" : "user",
    content,
  };
}

function buildValidationAgentConversation(
  messages: TgkMessageModel[],
  currentMessage: TgkMessageModel
): ValidationAgentConversationMessage[] {
  return [...messages, currentMessage]
    .map(toValidationAgentConversationMessage)
    .filter((message): message is ValidationAgentConversationMessage => message !== null);
}

function resolveRuleById(
  ruleId: string,
  stagedRules: Rule[],
  committedRules: Rule[],
  stagedRuleEdits: StagedEdit<Rule>[] = []
): Rule | undefined {
  // Pending edits to committed rules must win over the committed version, otherwise launching
  // a rule right after an edit_rule tool call would run the stale committed rule.
  const pendingEdit = stagedRuleEdits.find((edit) => edit.targetId === ruleId);
  if (pendingEdit) {
    return pendingEdit.patch;
  }
  return stagedRules.find((rule) => rule.id === ruleId) ?? committedRules.find((rule) => rule.id === ruleId);
}

function getDistinctValues(values: Array<string | undefined>): string[] {
  return Array.from(
    new Set(values.filter((value): value is string => typeof value === "string" && value.trim().length > 0))
  );
}

function warnIfConsistencyTagMissing(
  consistencyTags: RunResult["info"]["consistencyTags"],
  consistencyTag: NonNullable<RunResult["info"]["consistencyTags"]>[number] | undefined,
  ruleId: string,
  tagId: string,
  index: number
): void {
  if (consistencyTags != null && consistencyTag === undefined) {
    console.warn("[IDS-5945] buildLaunchReferencedTags: consistencyTag missing at index", {
      ruleId,
      tagId,
      index,
    });
  }
}

function buildStatusFields(consistencyTag: NonNullable<RunResult["info"]["consistencyTags"]>[number] | undefined) {
  if (consistencyTag?.status === "ERROR") {
    const fields: { status: string; errorKind?: ConsistencyTagErrorKind } = { status: "ERROR" };
    if (consistencyTag.errorKind !== undefined) {
      fields.errorKind = consistencyTag.errorKind;
    }
    return fields;
  }
  if (consistencyTag?.status) {
    return { status: consistencyTag.status };
  }
  return {};
}

function buildLaunchReferencedTags(rule: Rule, runResult: RunResult, allTags: Tag[]): LaunchResult["referencedTags"] {
  const tagsById = new Map(allTags.map((tag) => [tag.id, tag]));

  if (rule.type === RuleType.CONSISTENCY) {
    return (rule.tagIds ?? []).map((tagId, index) => {
      const tag = tagsById.get(tagId);
      // [IDS-5945] Index-alignment assumption: consistencyTags is built by buildConsistencyResult
      // in order of consistencyResults, which should mirror rule.tagIds. If that alignment drifts,
      // the entry at [index] may be undefined (Metis Risk 3).
      const consistencyTag = runResult.info.consistencyTags?.[index];
      warnIfConsistencyTagMissing(runResult.info.consistencyTags, consistencyTag, rule.id, tagId, index);
      const distinctValues = getDistinctValues((consistencyTag?.objects ?? []).map((object) => object.text));

      return {
        tagId,
        label: tag?.label ?? consistencyTag?.label ?? tagId,
        ...buildStatusFields(consistencyTag),
        ...(distinctValues.length > 0 ? { values: distinctValues } : {}),
      };
    });
  }

  return extractTagsFromScript(rule.script ?? "", allTags).map((tag) => ({
    tagId: tag.id,
    label: tag.label,
    value: tag.objects?.[0]?.text,
  }));
}

function getLaunchResultStatus(runStatus: RunResult["info"]["status"]): LaunchResult["status"] {
  if (runStatus === "VERIFIED") {
    return "VERIFIED";
  }

  if (runStatus === "FAILED") {
    return "FAILED";
  }

  return "CANNOT_EXECUTE";
}

function resolveLaunchRuleType(rule: Rule | undefined, runResult: RunResult): LaunchResult["ruleType"] {
  if (rule?.type === RuleType.CONSISTENCY || runResult.ruleType === RuleType.CONSISTENCY) {
    return RuleType.CONSISTENCY;
  }

  if (rule?.type === RuleType.TABLE_TOTALS || runResult.ruleType === RuleType.TABLE_TOTALS) {
    return RuleType.TABLE_TOTALS;
  }

  return RuleType.CUSTOM;
}

function buildLaunchResults(runResults: RunResult[], launchedRules: Rule[], allTags: Tag[]): LaunchResult[] {
  const rulesById = new Map(launchedRules.map((rule) => [rule.id, rule]));

  return runResults.map((runResult) => {
    const rule = rulesById.get(runResult.ruleId);
    const ruleType = resolveLaunchRuleType(rule, runResult);
    const status = getLaunchResultStatus(runResult.info.status);

    return {
      ruleId: runResult.ruleId,
      ruleType,
      status,
      referencedTags: rule ? buildLaunchReferencedTags(rule, runResult, allTags) : undefined,
      ...(status === "CANNOT_EXECUTE" ? { errorReason: getRunResultErrorReason(runResult) } : {}),
    };
  });
}

function buildFallbackLaunchResultsMessage(runResults: RunResult[]) {
  return createLaunchResultsMessage(
    runResults.map((runResult) => ({
      status: runResult.info.status,
      interpretationHtml: buildFallbackLaunchInterpretation(runResult),
    }))
  );
}

function nonEmptyOrUndefined<T>(arr: T[]): T[] | undefined {
  return arr.length > 0 ? arr : undefined;
}

/** Remaps tag-ids / script references inside a rule using the provided remap. */
function applyRemapToRule(rule: Rule, resolvedRemap: Map<string, string>): Rule {
  if (rule.type === RuleType.CONSISTENCY) {
    const resolvedTagIds = [...new Set((rule.tagIds ?? []).map((tagId) => resolvedRemap.get(tagId) ?? tagId))];
    return { ...rule, tagIds: resolvedTagIds };
  }
  let resolvedScript = rule.script ?? "";
  resolvedRemap.forEach((newId, oldId) => {
    resolvedScript = resolvedScript.split(`{{${oldId}}}`).join(`{{${newId}}}`);
  });
  return { ...rule, script: resolvedScript };
}

/** Applies the resolved remap to a staged rule edit patch. Returns the original patch when nothing changes. */
function applyRemapToStagedRulePatch(patch: Rule, resolvedRemap: Map<string, string>): Rule {
  if (resolvedRemap.size === 0) return patch;
  if (patch.type === RuleType.CONSISTENCY && patch.tagIds) {
    return {
      ...patch,
      tagIds: [...new Set(patch.tagIds.map((tagId) => resolvedRemap.get(tagId) ?? tagId))],
    };
  }
  if (patch.script) {
    let resolvedScript = patch.script;
    resolvedRemap.forEach((newId, oldId) => {
      resolvedScript = resolvedScript.split(`{{${oldId}}}`).join(`{{${newId}}}`);
    });
    return { ...patch, script: resolvedScript };
  }
  return patch;
}

async function applyMergedTagsUpdate(
  updatedTags: Tag[],
  tagIdRemap: Map<string, string>,
  editResult: EditTagResult,
  mergedTag: Tag
): Promise<Tag[]> {
  const { sourceTagId, destinationTagId } = editResult;
  if (!sourceTagId || !destinationTagId) return updatedTags;
  tagIdRemap.set(sourceTagId, destinationTagId);
  await remapAndPersistRules(sourceTagId, destinationTagId);
  const destinationExistsInState = updatedTags.some((t) => t.id === destinationTagId);
  const result = updatedTags.map((currentTag) => {
    if (currentTag.id === sourceTagId) return { ...currentTag, isDeleted: true };
    if (currentTag.id === destinationTagId) {
      if (currentTag.isDeleted) {
        return { ...currentTag, objects: mergedTag.objects, isDeleted: false };
      }
      const existingIds = new Set(currentTag.objects.map((o) => o.id));
      const newObjects = mergedTag.objects.filter((o) => o.id && !existingIds.has(o.id));
      return { ...currentTag, objects: [...currentTag.objects, ...newObjects], isDeleted: false };
    }
    return currentTag;
  });
  if (!destinationExistsInState) {
    result.push({ id: destinationTagId, label: mergedTag.label, objects: mergedTag.objects, isDeleted: false });
  }
  return result;
}

async function processExistingTag(
  tag: Tag,
  existingTag: Tag,
  updatedTags: Tag[],
  tagIdRemap: Map<string, string>
): Promise<Tag[]> {
  // When the matched existing tag is soft-deleted, this is a recreation: use only the
  // proposed objects, do not merge with the deleted tag's old objects.
  const mergedTag = existingTag.isDeleted
    ? { ...tag, objects: tag.objects, isDeleted: false as const }
    : (() => {
        const newObjects = tag.objects.filter(
          (objectSummary) =>
            objectSummary.id && existingTag.objects.findIndex((object) => object.id === objectSummary.id) === -1
        );
        return { ...tag, objects: [...existingTag.objects, ...newObjects] };
      })();
  const editResult: EditTagResult = await editTagInCustomXml(mergedTag);
  if (editResult.merged && editResult.sourceTagId && editResult.destinationTagId) {
    return applyMergedTagsUpdate(updatedTags, tagIdRemap, editResult, mergedTag);
  }
  return updatedTags.map((currentTag) => (currentTag.id === tag.id ? { ...mergedTag, isDeleted: false } : currentTag));
}

async function processNewTag(tag: Tag, updatedTags: Tag[], tagIdRemap: Map<string, string>): Promise<Tag[]> {
  const generatedId = generatePrefixedId("TAG");
  const resolvedId = await addTagToCustomXml({ ...tag, id: generatedId });
  tagIdRemap.set(tag.id, resolvedId);
  const mergedIndex = updatedTags.findIndex((currentTag) => currentTag.id === resolvedId);
  const result = [...updatedTags];
  if (mergedIndex !== -1) {
    const existingTag = result[mergedIndex];
    if (existingTag.isDeleted) {
      // Recreation of a soft-deleted tag: use only the proposed objects, do not merge.
      result[mergedIndex] = { id: resolvedId, label: tag.label, objects: tag.objects, isDeleted: false };
    } else {
      const newObjects = tag.objects.filter(
        (objectSummary) =>
          objectSummary.id && existingTag.objects.findIndex((object) => object.id === objectSummary.id) === -1
      );
      result[mergedIndex] = { ...existingTag, objects: [...existingTag.objects, ...newObjects] };
    }
  } else {
    result.push({ ...tag, id: resolvedId });
  }
  return result;
}

async function processStagedEdit(
  edit: StagedEdit<Tag>,
  existingTagsList: Tag[],
  updatedTags: Tag[],
  tagIdRemap: Map<string, string>,
  dispatch: AppDispatch
): Promise<Tag[]> {
  const committedTag = existingTagsList.find((t) => t.id === edit.targetId);
  if (!committedTag) {
    dispatch(
      addMessage(
        newBotSystemMsg(
          formatMessageWithParamsDefault(
            CAPTION_ASKAI_DISCARDED_TAG_EDIT,
            "Pending edit for tag '{tagId}' was discarded because the tag no longer exists.",
            { tagId: edit.targetId }
          )
        )
      )
    );
    return updatedTags;
  }
  try {
    const mergedTag = { ...committedTag, ...edit.patch };
    const editResult: EditTagResult = await editTagInCustomXml(mergedTag);
    if (editResult.merged && editResult.sourceTagId && editResult.destinationTagId) {
      return applyMergedTagsUpdate(updatedTags, tagIdRemap, editResult, mergedTag);
    }
    return updatedTags.map((t) => (t.id === edit.targetId ? mergedTag : t));
  } catch {
    dispatch(
      addMessage(
        newBotSystemMsg(
          formatMessageWithParamsDefault(
            CAPTION_ASKAI_DISCARDED_TAG_EDIT,
            "Pending edit for tag '{tagId}' was discarded because the tag no longer exists.",
            { tagId: edit.targetId }
          )
        )
      )
    );
    return updatedTags;
  }
}

/**
 * Merges committed and staged tags for rule launch, giving staged tags priority.
 * When an agent re-proposes a deleted committed tag (same id), the staged version
 * must shadow the deleted one so that getRuleIdToTags resolves it as a live tag.
 */
export function mergeTagsForLaunch(committedTags: Tag[], stagedTags: Tag[]): Tag[] {
  const stagedIds = new Set(stagedTags.map((t) => t.id));
  return [...committedTags.filter((t) => !stagedIds.has(t.id)), ...stagedTags];
}

export function useAskAiModal(props: AskAiProps): AskAiResult {
  const {
    onHide,
    documentId,
    tags = [],
    rules = [],
    updateTags = () => {},
    updateRules = () => {},
    coObjects = [],
  } = props;
  const nonDeletedTags = tags.filter((t) => !t.isDeleted);
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const messages = useAppSelector((state) => state.askAiChat.messages);
  const stagedTags = useAppSelector((state) => state.askAiChat.stagedTags);
  const stagedRules = useAppSelector((state) => state.askAiChat.stagedRules);
  const stagedTagEdits = useAppSelector((state) => state.askAiChat.stagedTagEdits);
  const stagedRuleEdits = useAppSelector((state) => state.askAiChat.stagedRuleEdits);
  const launchRuleIds = useAppSelector((state) => state.askAiChat.launchRuleIds);
  const [loading, setLoading] = useState<boolean>(false);
  const [showReviewSaveModal, setShowReviewSaveModal] = useState<boolean>(false);
  const [ragIndexUiState, setRagIndexUiState] = useState<RagIndexUiState>(EMPTY_RAG_INDEX_UI_STATE);
  const requestIdRef = useRef<number>(0);
  const lastRunResultsRef = useRef<RunResult[]>([]);

  useEffect(() => {
    if (!props.isShown || !documentId) {
      return;
    }

    let disposed = false;
    let prepareStarted = false;
    let unsubscribe: (() => void) | undefined;
    let prepareFallback: ReturnType<typeof setTimeout> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryAttempts = 0;
    // Bumped on every (re)subscribe attempt and on teardown; lets a late-resolving subscribeToHost
    // promise recognize it is stale (superseded by a newer attempt or the effect being disposed)
    // and close the EventSource it just opened instead of leaking it.
    let generation = 0;
    setRagIndexUiState(EMPTY_RAG_INDEX_UI_STATE);

    const startPrepare = () => {
      if (disposed || prepareStarted) {
        return;
      }

      prepareStarted = true;
      if (prepareFallback) {
        clearTimeout(prepareFallback);
      }
      void prepareRagIndex(documentId)
        .then((snapshot) => {
          if (!disposed) {
            setRagIndexUiState((current) => mergeRagIndexStatus(current, snapshot, true));
          }
        })
        .catch((error) => {
          console.error("[AskAiModalHook] Failed to prepare RAG index:", error);
        });
    };

    const clearRetryTimer = () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = undefined;
      }
    };

    // Re-subscribes explicitly (as opposed to relying on the eventsource library's own retry,
    // which never fires once readyState is CLOSED). Each call goes through subscribeToHost again,
    // which naturally re-resolves getHeaders(false) and so captures a fresh auth token.
    const startSubscription = () => {
      const myGeneration = ++generation;
      void subscribeToHost<RagIndexStatusChangedEvent>({
        paramPath: `/_sse/rag/index/document/${documentId}/status/subscribe`,
        replaceExisting: false,
        openCallback: () => {
          // A successful (re)open proves the endpoint is reachable again; forgive prior failures.
          retryAttempts = 0;
          startPrepare();
        },
        messageCallback: (event) => {
          if (!disposed && event.documentId === documentId) {
            setRagIndexUiState((current) => mergeRagIndexStatus(current, event));
          }
        },
        errorCallback: (error) => {
          console.error("[AskAiModalHook] RAG index status subscription failed:", error);
          if (error.terminal) {
            // Terminal (readyState CLOSED, e.g. 401/503): the library will not retry on its own.
            // Resync comes from the backend snapshot on the next subscribe, so prepare must NOT
            // be re-run here.
            scheduleRetry(myGeneration);
          } else {
            // Non-terminal transport errors keep the pre-existing recovery path.
            startPrepare();
          }
        },
      }).then((closeSubscription) => {
        if (disposed || myGeneration !== generation) {
          // Stale: either torn down, or superseded by a newer subscribe/retry attempt.
          closeSubscription();
          return;
        }
        unsubscribe = closeSubscription;
      });
    };

    const scheduleRetry = (forGeneration: number) => {
      if (disposed || forGeneration !== generation) {
        return;
      }
      if (retryAttempts >= RAG_SSE_RESUBSCRIBE_MAX_ATTEMPTS) {
        console.warn("[AskAiModalHook] RAG index status subscription exhausted retry attempts");
        return;
      }

      const delay = Math.min(RAG_SSE_RESUBSCRIBE_BASE_DELAY_MS * 2 ** retryAttempts, RAG_SSE_RESUBSCRIBE_MAX_DELAY_MS);
      retryAttempts += 1;
      clearRetryTimer();
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        if (disposed || forGeneration !== generation) {
          return;
        }
        unsubscribe?.();
        unsubscribe = undefined;
        startSubscription();
      }, delay);
    };

    startSubscription();
    if (!prepareStarted) {
      prepareFallback = setTimeout(startPrepare, 5000);
    }

    return () => {
      disposed = true;
      generation += 1;
      clearRetryTimer();
      if (prepareFallback) {
        clearTimeout(prepareFallback);
      }
      unsubscribe?.();
    };
  }, [documentId, props.isShown]);

  const openReviewSaveModal = useCallback(() => {
    setShowReviewSaveModal(true);
  }, []);

  const handleReviewModalClose = useCallback(() => {
    setShowReviewSaveModal(false);
  }, []);

  async function saveTags(reviewedData: PendingActionData, existingTagsList: Tag[]) {
    let updatedTags = existingTagsList ? [...existingTagsList] : [];
    const tagIdRemap = new Map<string, string>();
    const existingTags = new Map<string, Tag>((existingTagsList ?? []).map((tag) => [tag.id, tag]));

    const sortedTags = [...reviewedData.tags].sort((a, b) => {
      const aIsEdit = existingTags.has(a.id) ? 0 : 1;
      const bIsEdit = existingTags.has(b.id) ? 0 : 1;
      return aIsEdit - bIsEdit;
    });

    for (const tag of sortedTags) {
      // Skip new tags without any associated objects to prevent orphan tags
      if (!existingTags.has(tag.id) && (!tag.objects || tag.objects.length === 0)) {
        continue;
      }
      if (existingTags.has(tag.id)) {
        const existingTag = existingTags.get(tag.id);
        if (!existingTag) continue;
        updatedTags = await processExistingTag(tag, existingTag, updatedTags, tagIdRemap);
      } else {
        updatedTags = await processNewTag(tag, updatedTags, tagIdRemap);
      }
    }

    // Process stagedTagEdits: apply pending edits to committed tags
    const currentStagedTagEdits = store.getState().askAiChat.stagedTagEdits;
    for (const edit of currentStagedTagEdits) {
      updatedTags = await processStagedEdit(edit, existingTagsList, updatedTags, tagIdRemap, dispatch);
    }

    dispatch(clearStagedTagEdits());

    updateTags(updatedTags);
    return tagIdRemap;
  }

  async function saveRules(
    reviewedData: PendingActionData,
    tagIdRemap: Map<string, string>,
    existingRulesList: Rule[]
  ): Promise<Map<string, string>> {
    let updatedRules = existingRulesList ? [...existingRulesList] : [];
    const existingRuleIds = new Set((existingRulesList ?? []).map((rule) => rule.id));
    const committedRuleIds = new Map<string, string>();
    const resolvedRemap = resolveTransitiveRemaps(tagIdRemap);

    // Read fresh staged rule edits from the store before the remap dispatch updates them,
    // so that applyRemapToStagedRulePatch below still works on the original (un-remapped) patches.
    const currentStagedRuleEdits = store.getState().askAiChat.stagedRuleEdits;

    // Keep Redux state in sync: update stagedRuleEdits with the resolved remap
    if (resolvedRemap.size > 0) {
      dispatch(applyRemapToStagedRuleEdits(Object.fromEntries(resolvedRemap)));
    }

    // Apply tag remap to all existing rules so React state matches what
    // remapAndPersistRules already persisted to Custom XML during saveTags.
    if (resolvedRemap.size > 0) {
      updatedRules = updatedRules.map((rule) => applyRemapToRule(rule, resolvedRemap));
    }

    for (const rule of reviewedData.rules) {
      const resolvedRule = applyRemapToRule(rule, resolvedRemap);

      if (rule.id && existingRuleIds.has(rule.id)) {
        await editRuleInCustomXml(resolvedRule);
        updatedRules = updatedRules.map((currentRule) => (currentRule.id === rule.id ? resolvedRule : currentRule));
        committedRuleIds.set(rule.id, rule.id);
      } else {
        const newId = generatePrefixedId("RULE");
        await addRuleToCustomXml({ ...resolvedRule, id: newId });
        updatedRules.push({ ...resolvedRule, id: newId });
        committedRuleIds.set(rule.id, newId);
      }
    }

    // Process stagedRuleEdits: apply pending edits to committed rules
    for (const edit of currentStagedRuleEdits) {
      const committedRule = existingRulesList.find((r) => r.id === edit.targetId);
      if (!committedRule) {
        dispatch(
          addMessage(
            newBotSystemMsg(
              formatMessageWithParamsDefault(
                CAPTION_ASKAI_DISCARDED_RULE_EDIT,
                "Pending edit for rule '{ruleId}' was discarded because the rule no longer exists.",
                { ruleId: edit.targetId }
              )
            )
          )
        );
        continue;
      }
      // Apply the resolved remap to the patch in-memory (closure snapshot, not updated Redux state)
      const resolvedPatch = applyRemapToStagedRulePatch(edit.patch, resolvedRemap);
      const rule = { ...committedRule, ...resolvedPatch };
      try {
        await editRuleInCustomXml(rule);
        updatedRules = updatedRules.map((r) => (r.id === edit.targetId ? rule : r));
      } catch {
        dispatch(
          addMessage(
            newBotSystemMsg(
              formatMessageWithParamsDefault(
                CAPTION_ASKAI_DISCARDED_RULE_EDIT,
                "Pending edit for rule '{ruleId}' was discarded because the rule no longer exists.",
                { ruleId: edit.targetId }
              )
            )
          )
        );
      }
    }
    dispatch(clearStagedRuleEdits());

    updateRules(updatedRules);
    return committedRuleIds;
  }

  const saveTagsAndRules = useCallback(
    async (reviewedData: PendingActionData): Promise<string[]> => {
      // Read fresh staged rule edits before saveRules dispatches clearStagedRuleEdits,
      // so that the "Launch rule" button includes edited rule IDs after review-save.
      const editedRuleIds = store.getState().askAiChat.stagedRuleEdits.map((edit) => edit.targetId);
      const tagIdRemap = await saveTags(reviewedData, tags);
      const committedRuleIds = await saveRules(reviewedData, tagIdRemap, rules);
      const proposedRuleIds = reviewedData.rules
        .map((rule) => committedRuleIds.get(rule.id) ?? rule.id)
        .filter((ruleId): ruleId is string => Boolean(ruleId));
      return [...new Set([...proposedRuleIds, ...editedRuleIds])];
    },
    [dispatch, rules, stagedRuleEdits, stagedTagEdits, tags, updateRules, updateTags]
  );

  const handleReviewSave = useCallback(
    (reviewedData: PendingActionData) => {
      void (async () => {
        try {
          const committedRuleIds = await saveTagsAndRules(reviewedData);
          setShowReviewSaveModal(false);
          dispatch(clearStagingArea());
          dispatch(clearPendingActionData());
          dispatch(setLaunchRuleIds(committedRuleIds));
          dispatch(
            addMessage(
              newBotSystemMsg(
                formatMessageDefault(
                  CAPTION_SAVED_VALIDATION_AREA,
                  "Everything has been successfully saved to your validation area"
                )
              )
            )
          );
        } catch (error) {
          console.error("[AskAiModalHook] Failed to save tags and rules:", error);
          dispatch(
            addMessage(
              newBotSystemMsg(
                formatMessageDefault(CAPTION_ERROR_VALIDATION_AREA, "Error saving to your validation area")
              )
            )
          );
        }
      })();
    },
    [dispatch, saveTagsAndRules]
  );

  const hasStagedReviewData =
    stagedTags.length > 0 || stagedRules.length > 0 || stagedTagEdits.length > 0 || stagedRuleEdits.length > 0;

  const handleResponse = useCallback(
    async (response: ValidationAgentResponse): Promise<ProcessToolCallsResult> => {
      return processToolCalls(response.tool_calls ?? [], {
        dispatch,
        coObjects,
        tagIdAliases: new Map<string, string>(),
        stagedRules,
        committedRules: rules,
        stagedTags,
        committedTags: tags,
        stagedTagEdits,
        stagedRuleEdits,
        lastRunResults: lastRunResultsRef.current,
        onLaunchRules: () => undefined,
      });
    },
    [coObjects, dispatch, rules, stagedRuleEdits, stagedRules, stagedTagEdits, stagedTags, tags]
  );

  const sendAndInterpretResults = useCallback(
    async (runResults: RunResult[], resolvedRules: Rule[], allTags: Tag[], currentRequestId: number): Promise<void> => {
      try {
        const launchResults = buildLaunchResults(runResults, resolvedRules, allTags);
        const syntheticConversation = buildValidationAgentConversation(
          messages,
          newUserMsg(wrapSystemMessage("Interpret these launch results."))
        );
        console.info("[AskAI] handleLaunchRules: sending launch results to agent for interpretation");
        const response = await sendLaunchResults({
          documentId,
          conversation: syntheticConversation,
          launchResults,
          existingTags: nonDeletedTags,
          existingRules: rules,
          stagedTags,
          stagedRules,
          pendingTagEdits: nonEmptyOrUndefined(stagedTagEdits.map(({ targetId, patch }) => ({ targetId, patch }))),
          pendingRuleEdits: nonEmptyOrUndefined(stagedRuleEdits.map(({ targetId, patch }) => ({ targetId, patch }))),
        });
        if (currentRequestId !== requestIdRef.current) return;
        await handleResponse(response);
      } catch (error) {
        if (currentRequestId !== requestIdRef.current) return;
        console.error("[AskAiModalHook] Failed to interpret launch results:", error);
        if (runResults.length > 0) {
          console.info("[AskAI] Showing local run results as fallback");
          dispatch(addMessage(buildFallbackLaunchResultsMessage(runResults)));
        } else {
          const status = (error as AxiosError)?.response?.status;
          dispatch(
            addMessage(
              newBotSystemMsg(
                status === 504
                  ? formatMessage(CAPTION_CHAT_TIMEOUT)
                  : formatMessageDefault(CAPTION_CHAT_ERROR_GENERIC, "Chat failed")
              )
            )
          );
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [
      dispatch,
      documentId,
      handleResponse,
      messages,
      rules,
      stagedRuleEdits,
      stagedRules,
      stagedTagEdits,
      stagedTags,
      tags,
    ]
  );

  const handleLaunchRules = useCallback(
    async (ruleIds: string[]): Promise<void> => {
      setLoading(true);
      console.info("[AskAI] handleLaunchRules: launching rules:", ruleIds);
      setShowReviewSaveModal(false);
      dispatch(setLaunchRuleIds([]));
      lastRunResultsRef.current = [];

      const currentRequestId = requestIdRef.current;
      const resolvedRules = ruleIds
        .map((ruleId) => resolveRuleById(ruleId, stagedRules, rules, stagedRuleEdits))
        .filter((rule): rule is Rule => Boolean(rule));
      const allTags = mergeTagsForLaunch(tags, stagedTags);

      if (resolvedRules.length === 0) {
        setLoading(false);
        return;
      }

      let runResults: RunResult[] = [];

      try {
        const response = await runRules(ruleIds, resolvedRules, allTags);
        if (currentRequestId !== requestIdRef.current) return;
        runResults = response.results;
        lastRunResultsRef.current = runResults;
        console.info("[AskAI] handleLaunchRules: runRules completed, results count:", runResults.length);
      } catch (error) {
        if (currentRequestId !== requestIdRef.current) return;
        console.error("[AskAiModalHook] Failed to simulate rules:", error);
        dispatch(addMessage(newBotSystemMsg(formatMessage(CAPTION_ERROR_RULE_SIMULATION))));
        setLoading(false);
        return;
      } finally {
        if (currentRequestId !== requestIdRef.current) {
          setLoading(false);
        }
      }

      await sendAndInterpretResults(runResults, resolvedRules, allTags, currentRequestId);
    },
    [dispatch, sendAndInterpretResults, stagedRules, rules, tags, stagedTags, stagedRuleEdits]
  );

  const actionMessages = useMemo<TgkActionMessage[]>(() => {
    if (loading || (!hasStagedReviewData && launchRuleIds.length === 0)) {
      return [];
    }

    const actions: TgkActionMessage[] = [
      newUserActionLabel(formatMessageDefault(CAPTION_LABEL_SELECT_OPTION, "Select an option:")),
    ];

    if (launchRuleIds.length > 0) {
      actions.push(
        newUserActionMsg(
          launchRuleIds.length === 1
            ? formatMessageDefault(CAPTION_BUTTON_RUN_RULE, "Launch rule")
            : formatMessageWithParamsDefault(CAPTION_BUTTON_RUN_RULES, "Launch {count} rules", {
                count: launchRuleIds.length,
              }),
          () => {
            void handleLaunchRules(launchRuleIds);
          }
        )
      );
    }

    if (hasStagedReviewData) {
      actions.push(
        newUserActionMsg(formatMessageDefault(CAPTION_BUTTON_CHECK_AND_EDIT, "Check and edit tags & rule"), () => {
          openReviewSaveModal();
        })
      );
    }

    return actions;
  }, [handleLaunchRules, hasStagedReviewData, launchRuleIds, loading, openReviewSaveModal]);

  const handleBack = useCallback(() => {
    setShowReviewSaveModal(false);
    onHide();
  }, [onHide]);

  const resetChat = useCallback(() => {
    if (messages.length === 0) return;
    requestIdRef.current += 1;
    lastRunResultsRef.current = [];
    dispatch(resetMessages());
    setShowReviewSaveModal(false);
    setLoading(false);
  }, [dispatch, messages.length]);

  function buildTagResolutionErrorMessage(
    errors: UnresolvableTagError[],
    availableContentControlIds: number[]
  ): string {
    const errorDetails = errors
      .map(
        (e) =>
          `- Tag "${e.tagLabel}" (id: ${e.tagId}): contentControlIds [${e.invalidContentControlIds.join(", ")}] do not match any document objects`
      )
      .join("\n");
    return (
      `Error: the following proposed tags have invalid contentControlIds that don't match any document objects:\n${errorDetails}\n\n` +
      `Available contentControlIds are: [${availableContentControlIds.join(", ")}].\n` +
      `Please re-propose all tags and rules with correct contentControlIds from the available list above.`
    );
  }

  function buildDeletedTagReferenceErrorMessage(errors: DeletedTagReferenceError[]): string {
    const errorDetails = errors
      .map(
        (e) =>
          `- Rule "${e.ruleId}" references deleted tag IDs: [${e.deletedTagIds.join(", ")}]. These tags are deleted — you MUST either re-propose them with fresh IDs via propose_tags first, or pick a different non-deleted tag.`
      )
      .join("\n");
    return (
      `Error: the following rules reference deleted tag IDs:\n${errorDetails}\n\n` +
      `Please re-propose rules using only active (non-deleted) tag IDs, or first re-create the deleted tags via propose_tags.`
    );
  }

  const onMessageSend = useCallback(
    (message: TgkMessageModel): void => {
      void (async () => {
        setLoading(true);
        console.info("[AskAI] onMessageSend: sending message, conversation length:", messages.length + 1);
        const currentRequestId = requestIdRef.current;
        setShowReviewSaveModal(false);
        lastRunResultsRef.current = [];
        dispatch(setLaunchRuleIds([]));
        dispatch(addMessage(message));

        try {
          const conversation = buildValidationAgentConversation(messages, message);
          console.info("[AskAI] Calling executeValidationAgent:", {
            eventType: "USER_MESSAGE",
            conversationLength: conversation.length,
          });
          const response = await executeValidationAgent({
            documentId,
            conversation,
            existingTags: nonDeletedTags,
            existingRules: rules,
            stagedTags,
            stagedRules,
            eventType: "USER_MESSAGE",
            pendingTagEdits: nonEmptyOrUndefined(stagedTagEdits.map(({ targetId, patch }) => ({ targetId, patch }))),
            pendingRuleEdits: nonEmptyOrUndefined(stagedRuleEdits.map(({ targetId, patch }) => ({ targetId, patch }))),
          });
          if (currentRequestId !== requestIdRef.current) return;
          console.info("[AskAI] executeValidationAgent response received:", {
            toolCallsCount: response.tool_calls?.length ?? 0,
          });

          let processResult = await handleResponse(response);

          // Retry loop for unresolvable tags and deleted tag references
          const MAX_RETRY_ATTEMPTS = 2;
          let attempts = 0;
          let lastToolCalls = response.tool_calls ?? [];
          let retryConversation = conversation;

          while (!processResult.success && attempts < MAX_RETRY_ATTEMPTS) {
            attempts++;

            const failure = processResult as Extract<ProcessToolCallsResult, { success: false }>;
            let errorMessage: string;

            if ("deletedTagReferenceErrors" in failure) {
              console.warn(`[AskAI] Deleted tag reference errors, retry ${attempts}/${MAX_RETRY_ATTEMPTS}`);
              errorMessage = buildDeletedTagReferenceErrorMessage(failure.deletedTagReferenceErrors);
            } else {
              console.warn(`[AskAI] Tag resolution failed, retry ${attempts}/${MAX_RETRY_ATTEMPTS}`);
              const availableContentControlIds = coObjects
                .map((obj) => obj.contentControlId)
                .filter((id): id is number => id !== undefined);
              errorMessage = buildTagResolutionErrorMessage(failure.unresolvableTagErrors, availableContentControlIds);
            }

            retryConversation = [
              ...retryConversation,
              { role: "assistant" as const, content: JSON.stringify(lastToolCalls) },
              { role: "user" as const, content: wrapSystemMessage(errorMessage) },
            ];

            const retryResponse = await executeValidationAgent({
              documentId,
              conversation: retryConversation,
              existingTags: nonDeletedTags,
              existingRules: rules,
              stagedTags,
              stagedRules,
              eventType: "USER_MESSAGE",
              pendingTagEdits: nonEmptyOrUndefined(stagedTagEdits.map(({ targetId, patch }) => ({ targetId, patch }))),
              pendingRuleEdits: nonEmptyOrUndefined(
                stagedRuleEdits.map(({ targetId, patch }) => ({ targetId, patch }))
              ),
            });
            if (currentRequestId !== requestIdRef.current) return;
            console.info("[AskAI] Retry response received:", {
              toolCallsCount: retryResponse.tool_calls?.length ?? 0,
              attempt: attempts,
            });

            lastToolCalls = retryResponse.tool_calls ?? [];
            processResult = await handleResponse(retryResponse);
          }

          if (!processResult.success) {
            console.error("[AskAI] Tag resolution failed after all retries, showing error to user");
            dispatch(addMessage(newBotSystemMsg(formatMessageDefault(CAPTION_CHAT_ERROR_GENERIC, "Chat failed"))));
          }
        } catch {
          if (currentRequestId !== requestIdRef.current) return;
          dispatch(addMessage(newBotMsg(formatMessage(CAPTION_CHAT_ERROR_GENERIC))));
        } finally {
          if (currentRequestId === requestIdRef.current) {
            setLoading(false);
          }
        }
      })();
    },
    [
      coObjects,
      dispatch,
      documentId,
      handleResponse,
      messages,
      rules,
      stagedRuleEdits,
      stagedRules,
      stagedTagEdits,
      stagedTags,
      tags,
    ]
  );

  const welcomeMessages = [newBotSystemMsg(formatMessage("cof.validation.message.chatwelcome"))];
  const pendingActionData = hasStagedReviewData ? { tags: stagedTags, rules: stagedRules } : null;

  return {
    handleBack,
    messages: [...welcomeMessages, ...messages, ...actionMessages],
    loading,
    onMessageSend,
    pendingActionData,
    hasStagedReviewData,
    showReviewSaveModal,
    onReviewModalClose: handleReviewModalClose,
    onReviewSave: handleReviewSave,
    resetChat,
    ragIndexStatus: ragIndexUiState.status,
    ragIndexSentAt: ragIndexUiState.sentAt,
    ragIndexUploadObserved: ragIndexUiState.uploadObserved,
  };
}
